import { LucideArrowBigUp, LucideArrowUp, LucideBlocks, LucideCheck, LucideChevronRight, LucideChrome, LucideGlobe, LucideGrid, LucideHourglass, LucideLoader, LucideMic, LucideMic2, LucideMicrowave, LucideMoon, LucideScroll, LucideSun, LucideWaves } from "lucide-react";
import styles from "../styles/Home.module.css";
import { useRef, useState, useEffect, useMemo } from "react";
import thumbnail from "./thumbnail.jpg";

const darkThemeColors = {
    primaryColor: "rgba(10,10,20,1)",
    secondaryColor: "#fff",
    tertiaryColor: "rgba(255,0,70,1)",
    quaternaryColor: "rgba(40, 40, 50, 1)",
    quinaryColor: "rgba(10, 10, 10, 1)",
    underLine: "rgba(160,160,170,0.2)",
    hoverText: "rgba(190,190,190,1)",
    secondaryColorLighter: "rgba(255,255,255,0.8)",
    boxShadow1: "-1px -4px 70px -15px rgba(0, 0, 0, 1)",
}

const lightThemeColors = {
    primaryColor: "#fff",
    secondaryColor: "#000",
    tertiaryColor: "rgba(255,0,70,1)",
    quaternaryColor: "rgba(230, 230, 240, 1)",
    quinaryColor: "rgba(200, 200, 210, 1)",
    underLine: "rgba(40,40,40,0.2)",
    hoverText: "rgba(140,140,140,1)",
    secondaryColorLighter: "rgba(0,0,0,0.8)",
    boxShadow1: "0px 0px 30px 10px rgba(240, 240, 245, 1)",
}

function setLightThemeRoot() {
    document.documentElement.style.setProperty("--primary-color", lightThemeColors.primaryColor);
    document.documentElement.style.setProperty("--secondary-color", lightThemeColors.secondaryColor);
    document.documentElement.style.setProperty("--tertiary-color", lightThemeColors.tertiaryColor);
    document.documentElement.style.setProperty("--quaternary-color", lightThemeColors.quaternaryColor);
    document.documentElement.style.setProperty("--quinary-color", lightThemeColors.quinaryColor);
    document.documentElement.style.setProperty("--underLine", lightThemeColors.underLine);
    document.documentElement.style.setProperty("--hoverText", lightThemeColors.hoverText);
    document.documentElement.style.setProperty("--secondaryColorLighter", lightThemeColors.secondaryColorLighter);
    document.documentElement.style.setProperty("--boxShadow1", lightThemeColors.boxShadow1);
}

function setDarkThemeRoot() {
    document.body.classList.add("dark-theme");
    document.documentElement.style.setProperty("--primary-color", darkThemeColors.primaryColor);
    document.documentElement.style.setProperty("--secondary-color", darkThemeColors.secondaryColor);
    document.documentElement.style.setProperty("--tertiary-color", darkThemeColors.tertiaryColor);
    document.documentElement.style.setProperty("--quaternary-color", darkThemeColors.quaternaryColor);
    document.documentElement.style.setProperty("--quinary-color", darkThemeColors.quinaryColor);
    document.documentElement.style.setProperty("--underLine", darkThemeColors.underLine);
    document.documentElement.style.setProperty("--hoverText", darkThemeColors.hoverText);
    document.documentElement.style.setProperty("--secondaryColorLighter", darkThemeColors.secondaryColorLighter);
    document.documentElement.style.setProperty("--boxShadow1", darkThemeColors.boxShadow1);

}

function generateFormattedString() {
    const characters = '0123456789abcdef';
    let formattedString = '';

    for (let i = 0; i < 32; i++) {
        formattedString += characters[Math.floor(Math.random() * characters.length)];

        if (i === 7 || i === 11 || i === 15 || i === 19) {
            formattedString += '-';
        }
    }

    return formattedString;
}

const splitMessageIntoChunks = async (message, chunkSize, firstException = null) => {
    let chunks = [];
    let start = 0;
    let count = 0;

    while (start < message.length) {
        let chunk = message.slice(start, start + chunkSize);
        if (firstException && count === 0) {
            chunk = message.slice(start, start + firstException);
        }
        chunks.push(chunk);
        start += chunkSize;
        count += 1;
    }

    return chunks;
};

export default function Home() {
    const [auth, setAuth] = useState(null);
    const inputRef = useRef(null);
    const keyRef = useRef(null);
    const [statusText, setStatusText] = useState("");
    const [statusColor, setStatusColor] = useState("rgba(0,0,0,0)");
    const [toSummarizeYtAuthor, setToSummarizeYtAuthor] = useState("");
    const [toSummarizeYtTitle, setToSummarizeYtTitle] = useState("");
    const [toSummarizeYtThumbnail, setToSummarizeYtThumbnail] = useState("");
    const [toSummarizeYtLength, setToSummarizeYtLength] = useState("");
    const [toSummarizeYtId, setToSummarizeYtId] = useState("");
    const [messagesDisplay, setMessagesDisplay] = useState([]);
    const [videoLoaded, setVideoLoaded] = useState(false);
    const [transcript, setTranscript] = useState(false);
    const [loadingSummary, setLoadingSummary] = useState(false);
    const [theme, setTheme] = useState("dark");
    const [writeSummary, setWriteSummary] = useState(false);
    const [languageSelected, setLanguageSelected] = useState({ language: "English", flag: "🇬🇧" });
    const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
    const [temperature, setTemperature] = useState(0);
    const [model, setModel] = useState("gpt-3.5-turbo");
    const [prompt, setPrompt] = useState("");
    const promptRef = useRef(null);
    const [chunkSize, setChunkSize] = useState(10000);

    const [message, setMessage] = useState('');
    const doneRef = useRef({ done: false })
    const [activated, setActivated] = useState(false);
    const letMeSeeRef = useRef(null);
    const getStartedRef = useRef(null);

    const finalMessage = `Introduction: Outlining the Three Paradoxes
    The Münchhausen Trilemma: This thought experiment posits that it is impossible to prove any truth.
    The Problem of the Criterion: This revolves around the dilemma of knowing something and the method of knowing it.
    The Question of Philosophical Progress: This questions whether any progress can be made in philosophy given that absolute knowledge seems unattainable.\n\n\n\n
    Chapter 1: The Münchhausen Trilemma
    Origin: Inspired by a fictional tale of Baron Münchhausen, who purportedly pulled himself and his horse out of a mire by his own hair. This impossible act symbolizes the difficulty of establishing foundational truths.
    Dilemma Explained: Introduced by German philosopher Hans Albert in 1968, the trilemma contends that it's impossible to prove anything as absolutely true. We are like the Baron, trying to escape uncertainty with no solid footing.
    Three Ways of Justification:
    Circular Reasoning: Using one proposition to prove another, which presupposes the first. E.g., "God exists because the Bible says so, and the Bible is true because it's the work of God."
    Infinite Regress: Justifying a claim with another claim that then needs further justification, continuing indefinitely. E.g., answering "Why does Earth exist?" leads to an infinite chain of "whys".
    Axiomatic Statements: Simply asserting something as true without evidence. E.g., "The car is red because I see it as red."
    Implication: Since all three justifications are problematic, absolute knowledge may be impossible.\n\n\n\n
    Chapter 2: The Problem of the Criterion
    Central Questions: "What do we know?" and "How do we know?"
    Knowledge Defined: A justified, true belief.
    Dilemma Explained: To truly know something, we need to answer at least one of the central questions. But, the answer to each question seems dependent on the answer to the other, creating a loop of uncertainty. For instance, we can't know what is true without a method to determine truth, and we can't determine a method of truth without already knowing something to be true.
    Implication: This paradox reveals a foundational problem in epistemology, questioning the basis of our knowledge.\n\n\n\n
    Chapter 3: The Question of Philosophical Progress
    Central Question: If we can't possess absolute knowledge, can there be any philosophical progress?
    Wittgenstein's Perspective: Philosopher Ludwig Wittgenstein believed traditional philosophy to be futile, equating philosophers to flies trapped in a bottle. For him, most philosophical problems might not be genuine, and those seeking conclusive answers are trapped in a self-imposed dilemma.
    Nature of Philosophy: Philosophy isn't just about finding definitive truths. It's about inquiry, self-understanding, connection, and embracing doubt. As Richard Feynman posited, living with doubt can be more enriching than having potentially wrong answers.
    Implication: Philosophical progress might not be about uncovering absolute truths but about exploring, connecting, and understanding our own limitations in the quest for knowledge.\n\n\n\n
    Closing Thought:
    While we might all be trapped within certain philosophical confines, subjects like science, math, music, art, and philosophy itself can enrich our existence and deepen our connections with others.`

    const activatedRef = useRef({ activated: false });

    const [updater, setUpdater] = useState(false);

    useEffect(() => {
        if (activatedRef.current.activated) {
            if (message.length === finalMessage.length) {
                doneRef.current.done = true;
                setUpdater(true);
                return;
            }

            if (!doneRef.current.done) {
                const timer = setTimeout(() => {
                    setMessage(finalMessage.substring(0, message.length + 1));
                }, 10);

                // Cleanup timer to avoid memory leaks
                return () => clearTimeout(timer);
            }
        }
    }, [message]);

    const [previewStatus, setPreviewStatus] = useState(false);


    useEffect(() => {
        if (previewStatus && !activatedRef.current.activated) {
            activatedRef.current.activated = true;
            setMessage("I")
        }
    }, [previewStatus])

    let stopRequested = false;

    useEffect(() => {
        if (writeSummary) {
            const filteredItems = messagesDisplay.filter(item => item.from === 'assistant');
            const accumulatedText = filteredItems.reduce((acc, item) => acc + item.message, '');

            const video_id = toSummarizeYtId;
            const summary_text = accumulatedText;
            const video_title = toSummarizeYtTitle;
            const video_author = toSummarizeYtAuthor;
            const summary_language = languageSelected;
            const user_os = "web";

            const response = fetch(`https://us-central1-vidsummize.cloudfunctions.net/writeNewSummaryWeb`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ video_id, summary_text, video_title, video_author, summary_language, user_os }),
            });

            setWriteSummary(false);
        }
    }, [writeSummary]);

    const convertSecondsToMinutesAndSeconds = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const secondsLeft = seconds % 60;

        return `${minutes}:${secondsLeft < 10 ? "0" + secondsLeft : secondsLeft}`;
    }

    async function getVideoDetails(videoId) {
        const response = await fetch(`https://us-central1-vidsummize.cloudfunctions.net/getTranscript`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ video_id: videoId }),
        });

        const json = await response.json();

        return { transcript: json.transcript, videoDetails: json.videoDetails };
    }

    const extractVideoId = (url) => {
        let videoId;

        if (url.includes("youtu.be")) {
            videoId = url.split("youtu.be/")[1];
        } else {
            const regex = /(?<=v=)[\w-]+/;
            const match = url.match(regex);

            if (!match || !match[0]) {
                if (url.length < 11) {
                    return;
                }
                videoId = url;
            } else {
                videoId = match[0];
            }
        }

        return videoId;
    }

    useEffect(() => {
        const hasAuth = localStorage.getItem("vidsummize_auth");
        const theme = localStorage.getItem("vidsummize_theme");
        const language = localStorage.getItem("vidsummize_language");

        if (!hasAuth) {
            setAuth(false);
        } else {
            setAuth(hasAuth);
        }

        if (!theme) {
            localStorage.setItem("vidsummize_theme", "dark");
            setDarkThemeRoot();
        } else {
            if (theme === "dark") {
                setDarkThemeRoot();
                setTheme("dark");
            } else {
                setLightThemeRoot();
                setTheme("light");
            }
        }

        if (!language) {
            localStorage.setItem("vidsummize_language", JSON.stringify({ language: "English", flag: "🇬🇧" }));
            setPrompt(translations["perMessagePrompt"]["English"]);
        } else {
            setLanguageSelected(JSON.parse(language));
            setPrompt(translations["perMessagePrompt"][JSON.parse(language).language]);
        }
    }, [])

    const handleSubmit = async (e) => {
        if (e) {
            e.preventDefault();
        }

        if (loadingSummary) return;
        const input = inputRef.current.value;
        const videoId = await extractVideoId(input);

        if (!videoId) {
            setStatusColor("rgba(255,0,70,1");
            setStatusText("Invalid URL");

            setTimeout(() => {
                setStatusColor("rgba(0,0,0,0)");
                setStatusText("");
            }, 3000);
            return;
        }

        setStatusColor("rgba(255,200,0,1)");
        setStatusText("Loading Transcript...")

        const { transcript, videoDetails } = await getVideoDetails(videoId);
        const { duration, channel, title, thumbnail, status } = videoDetails;

        if (!transcript) {
            setStatusColor("rgba(255,0,70,1");
            setStatusText("Cannot summarize. Video has no captions.");
            return;
        }

        setStatusColor("rgba(0,255,100,1");
        setStatusText("Fetched Transcript!");

        setToSummarizeYtAuthor(channel);
        setToSummarizeYtTitle(title);
        setToSummarizeYtThumbnail(thumbnail);
        setToSummarizeYtLength(duration);
        setToSummarizeYtId(videoId);
        setTranscript(transcript);
        setVideoLoaded(true);

        // inputRef.current.value = "";
    }

    const [supportedLanguages, setSupportedLanguages] = useState([
        {
            language: "English",
            flag: "🇬🇧",
            supported: true,
            limited: false,
        },
        {
            language: "Español",
            flag: "🇪🇸",
            supported: true,
            limited: false,
        },
        {
            language: "Pусский",
            flag: "🇷🇺",
            supported: true,
            limited: false,
        },
        {
            language: "Português",
            flag: "🇧🇷",
            supported: true,
            limited: false,
        },
        {
            language: "Deutsch",
            flag: "🇩🇪",
            supported: true,
            limited: false,
        },
        {
            language: "Français",
            flag: "🇫🇷",
            supported: true,
            limited: false,
        },
        {
            language: "Italiano",
            flag: "🇮🇹",
            supported: true,
            limited: false,
        },
        {
            language: "日本語",
            flag: "🇯🇵",
            supported: true,
            limited: true,
            limitedText: "限られた精度",
        },
        {
            language: "中文",
            flag: "🇨🇳",
            supported: true,
            limited: true,
            limitedText: "有限的精度",
        },
        {
            language: "العربية",
            flag: "🇸🇦",
            supported: true,
            limited: true,
            limitedText: "دقة محدودة",
        },
        {
            language: "हिंदी",
            flag: "🇮🇳",
            supported: true,
            limited: true,
            limitedText: "सीमित अनुभव",
        },
        {
            language: "한국어",
            flag: "🇰🇷",
            supported: true,
            limited: true,
            limitedText: "제한된 정확도",
        },
        {
            language: "اردو",
            flag: "🇵🇰",
            supported: true,
            limited: true,
            limitedText: "محدود درستگی",
        },
        {
            language: "Bahasa Melayu",
            flag: "🇲🇾",
            supported: true,
        },
        {
            language: "Bahasa Indonesia",
            flag: "🇮🇩",
            supported: true,
        },
        {
            language: "Tiếng Việt",
            flag: "🇻🇳",
            supported: true,
        },
        {
            language: "Nederlands",
            flag: "🇳🇱",
            supported: false,
        },
        {
            language: "Svenska",
            flag: "🇸🇪",
            supported: false,
        },
        {
            language: "Türkçe",
            flag: "🇹🇷",
            supported: false,
        },
        {
            language: "Українська",
            flag: "🇺🇦",
            supported: false,
        },
        {
            language: "Slovenský",
            flag: "🇸🇰",
            supported: true,
        },
        {
            language: "Polski",
            flag: "🇵🇱",
            supported: false,
        },
    ]);

    const [translations, setTranslations] = useState({
        perMessagePrompt: {
            "English": "summarize this text using bullet points, give it a big, bold heading as well. make it: easy to read and exhaustive but also provide context on topics or concepts the reader might not know of if necessary. remember that you heading should be separated by two new lines from the bullet points.",
            "Pусский": "Подведите итоги этого текста, используя маркеры для списка, дайте ему крупный и яркий заголовок. Сделайте его легко читаемым и исчерпывающим, но также предоставьте контекст по темам или концепциям, которые читатель может не знать, если это необходимо. Помните, что ваш заголовок должен отделяться двумя новыми строками от маркеров списка.",
            "Français": "Résumez ce texte en utilisant des puces, donnez-lui un grand titre en gras. Rendez-le facile à lire et exhaustif, mais fournissez également du contexte sur les sujets ou les concepts que le lecteur pourrait ne pas connaître si nécessaire. N'oubliez pas que votre titre doit être séparé de deux nouvelles lignes des puces.",
            "Deutsch": "Fassen Sie diesen Text mit Aufzählungspunkten zusammen, geben Sie ihm eine große, fette Überschrift. Machen Sie ihn leicht lesbar und umfassend, aber geben Sie auch Kontext zu Themen oder Konzepten, die der Leser möglicherweise nicht kennt, wenn nötig. Denken Sie daran, dass Ihre Überschrift durch zwei neue Zeilen von den Aufzählungspunkten getrennt sein sollte.",
            "Español": "Resuma este texto utilizando viñetas, déle un título grande y en negrita. Hágalo fácil de leer y exhaustivo, pero proporcione también contexto sobre temas o conceptos que el lector podría no conocer si es necesario. Recuerde que su título debe estar separado por dos nuevas líneas de las viñetas.",
            "Italiano": "Sommari questo testo utilizzando el elenco puntato, dando un grande titolo in grassetto. Rendilo facile da leggere ed esaustivo, ma fornisca anche contesto sui temi o sui concetti che il lettore potrebbe non conoscere, se necessario. Ricorda che il tuo titolo deve essere separato da due nuove linee dall'elenco puntato.",
            "Slovenský": "Zhrňte tento text pomocou odrážkového zoznamu, dajte mu veľký, tučný nadpis. Urobte ho ľahko čitateľným a podrobným, ale ak je to potrebné, poskytnite tiež kontext k témam alebo konceptom, ktoré by mohli byť pre čitateľa neznáme. Pamätajte si, že váš nadpis by mal byť oddelený dvoma novými riadkami od odrážkového zoznamu.",
            "中文": "用项目符号总结这个文本，同时加上一个大而醒目的标题。使其易于阅读和详尽，但如果必要，还要提供读者可能不了解的主题或概念的上下文。请记住，你的标题应与项目符号之间用两个新行分隔",
            "한국어": "글머리 기호를 사용하여이 텍스트를 요약하고 크고 굵은 제목을 제공합니다. 쉽게 읽고 철저하게 만드는 것뿐만 아니라 필요한 경우 독자가 모르는 주제나 개념에 대한 문맥도 제공합니다. 제목은 글머리 기호에서 두 개의 새 줄로 분리해야합니다.",
            "हिंदी": "बुलेट बिंदुओं का उपयोग करके इस पाठ का सारांश दें, उसे एक बड़ा, बोल्ड हेडिंग भी दें। यह आसान और विस्तृत बनाएँ, लेकिन आवश्यक होने पर पाठक को विषयों या अवधारणाओं के संदर्भ में भी प्रदान करें जो वह नहीं जानता हो सकता है। याद रखें कि आपका हेडिंग बुलेट बिंदुओं से दो नए लाइनों से अलग होना चाहिए।",
            "العربية": "قم بتلخيص هذا النص باستخدام علامات النقط، وتزويده بعنوان كبير وجريء. جعله سهل القراءة وشاملًا وكذلك توفير السياق اللازم للموضوعات أو المفاهيم التي قد لا يكون للقارئ معرفة بها إن لزم الأمر. تذكر أن يجب فصل العنوان بمسافتين من العلامات.",
            "اردو": "اس متن کو بلیٹ پوائنٹس کی مدد سے خلاصہ کریں، اسے بڑا، جریئہ عنوان بھی دیں۔ اسے آسان پڑھنے کے ساتھ مکمل بنائیں، لیکن ضرورت پڑنے پر قارئین کو موضوعات یا تصورات کے سیاق کے بارے میں بھی معلومات فراہم کریں جو وہ نہیں جانتے ہوسکتی ہیں۔ یاد رکھیں کہ آپ کا عنوان بلیٹ پوائنٹس سے دو نئی لائنز کے ذریعے الگ ہونا چاہئے۔",
            "Português": "Resuma este texto usando marcadores de pontos, dê um título grande e em negrito também. Torne-o fácil de ler e exaustivo, mas também forneça contexto sobre tópicos ou conceitos que o leitor possa não conhecer, se necessário. Lembre-se de que seu título deve ser separado por duas novas linhas dos marcadores de pontos.",
            "Indonesian": "Ringkas teks ini dengan menggunakan poin-poin, berikan judul yang besar dan tebal. Mudah dibaca dan komprehensif, tetapi juga berikan konteks pada topik atau konsep yang mungkin tidak diketahui pembaca jika perlu. Ingatlah bahwa judul harus dipisahkan oleh dua baris baru dari poin-poin.",
            "Bahasa Melayu": "Ringkaskan teks ini dengan menggunakan titik-titik, berikan tajuk yang besar dan tebal. Buat mudah dibaca dan menyeluruh, tetapi berikan konteks tentang topik atau konsep yang pembaca mungkin tidak tahu jika diperlukan. Ingat bahawa tajuk anda perlu dipisahkan dengan dua baris baru dari titik-titik.",
            "Bahasa Indonesia": "Ringkas teks ini dengan menggunakan poin-poin, berikan judul yang besar dan tebal. Mudah dibaca dan komprehensif, tetapi juga berikan konteks pada topik atau konsep yang mungkin tidak diketahui pembaca jika perlu. Ingatlah bahwa judul harus dipisahkan oleh dua baris baru dari poin-poin.",
            "Tiếng Việt": "Tóm tắt văn bản này bằng cách sử dụng các điểm đánh dấu, đưa ra tiêu đề lớn và đậm. Làm cho nó dễ đọc và toàn diện, nhưng cũng cung cấp ngữ cảnh về các chủ đề hoặc khái niệm mà độc giả có thể không biết nếu cần thiết. Nhớ rằng tiêu đề của bạn phải được phân tách bằng hai dòng mới từ các điểm đánh dấu.",
        },
        system: {
            "English": "ONLY ANSWER IN ENGLISH",

            // Spanish ONLY ANSWER IN SPANISH
            "Español": "SOLO RESPONDA EN ESPAÑOL",

            // Russian ONLY ANSWER IN RUSSIAN
            "Pусский": "ОТВЕТЬТЕ ТОЛЬКО НА РУССКОМ",

            // French ONLY ANSWER IN FRENCH
            "Français": "RÉPONDEZ UNIQUEMENT EN FRANÇAIS",

            // German ONLY ANSWER IN GERMAN
            "Deutsch": "ANTWORTEN SIE NUR AUF DEUTSCH",

            // Italian ONLY ANSWER IN ITALIAN
            "Italiano": "RISPOSTA SOLO IN ITALIANO",

            // Japanese ONLY ANSWER IN JAPANESE
            "日本語": "日本語でのみ回答してください",

            // Portuguese ONLY ANSWER IN PORTUGUESE
            "Português": "RESPOSTA APENAS EM PORTUGUÊS",

            // Chinese (Traditional) ONLY ANSWER IN CHINESE (TRADITIONAL)
            "中文": "只回答中文",

            // Hindi ONLY ANSWER IN HINDI
            "हिंदी": "हिंदी में ही जवाब दें",

            // Arabic ONLY ANSWER IN ARABIC
            "العربية": "الرد فقط باللغة العربية",

            // Korean ONLY ANSWER IN KOREAN
            "한국어": "한국어로만 답하세요",

            // Urdu ONLY ANSWER IN URDU
            "اردو": "صرفا اردو میں جواب دیں",

            // Vietnamese ONLY ANSWER IN VIETNAMESE
            "Tiếng Việt": "Chỉ trả lời bằng tiếng Việt",

            // Malay ONLY ANSWER IN MALAY
            "Bahasa Melayu": "Hanya jawab dalam Bahasa Melayu",

            // Indonesian ONLY ANSWER IN INDONESIAN
            "Bahasa Indonesia": "Hanya jawab dalam Bahasa Indonesia",

            // Slovak ONLY ANSWER IN SLOVAK slovensky
            "Slovenský": "ODPOVIEDAŤ LEN NA SLOVENSKY",

            // Vietnamese ONLY ANSWER IN VIETNAMESE
            "Tiếng Việt": "Chỉ trả lời bằng tiếng Việt",
        },
        summarizeButtonText: {
            "English": "Summarize", // English
            "Español": "Resumir", // Spanish
            "Pусский": "Итог", // Russian
            "Français": "Résumer.", // French
            "Deutsch": "Zusammenfassen", // German
            "Italiano": "Riassumi", // Italian
            "日本語": "要約する", // Japanese
            "Português": "Resumir", // Portuguese
            "中文": "总结", // Chinese (Traditional)
            "हिंदी": "सारांश", // Hindi
            "العربية": "ملخص", // Arabic
            "한국어": "요약", // Korean
            "اردو": "خلاصہ", // Urdu
            "Bahasa Melayu": "Ringkasan", // Malay
            "Bahasa Indonesia": "Ringkasan", // Indonesian
            "Tiếng Việt": "Tóm tắt", // Vietnamese
            "Slovenský": "Zhrnutie", // Slovak
        },
        summarizeButtonInUseText: {
            "English": "Wait until finished",
            "Español": "Esperar hasta que termine",
            "Pусский": "Ждите, пока закончится",
            "Français": "Attendre jusqu'à ce que cela soit terminé.",
            "Deutsch": "Warte bis es beendet ist",
            "Italiano": "Aspetta finché non è finito",
            "日本語": "終わるまで待つ",
            "Português": "Espere até terminar",
            "中文": "等到完成",
            "বাংলা": "শেষ হওয়ার অপেক্ষা করুন",
            "हिंदी": "समाप्त होने तक प्रतीक्षा करें",
            "한국어": "끝날 때까지 기다리십시오",
            "اردو": "ختم ہونے تک انتظار کریں",
            "العربية": "انتظر حتى ينتهي",
            "Bahasa Melayu": "Tunggu sehingga selesai",
            "Bahasa Indonesia": "Tunggu sampai selesai",
            "Tiếng Việt": "Đợi cho đến khi hoàn thành",
            "Slovenský": "Počkajte, kým sa dokončí",
        },
        chunkSizes: {
            "English": 15500,
            "Español": 14000,
            "Français": 14000,
            "Deutsch": 14000,
            "Italiano": 14000,
            "Pусский": 10000,
            "Português": 14000,
            "日本語": 14000, // Japanese
            "中文": 10000, // Chinese (Traditional)
            "हिंदी": 10000, // Hindi
            "العربية": 14000, // Arabic
            "한국어": 10000, // Korean
            "اردو": 10000,// Urdu
            "Bahasa Melayu": 10000, // Malay
            "Bahasa Indonesia": 10000, // Indonesian
            "Tiếng Việt": 10000, // Vietnamese
            "Slovenský": 10000, // Slovak
        },
        chapter: {
            "English": "Chapter", // English
            "Español": "Capítulo", // Spanish
            "Français": "Chapitre", // French
            "Deutsch": "Kapitel", // German
            "Italiano": "Capitolo", // Italian
            "Pусский": "Глава", // Russian     
            "Português": "Capítulo", // Portuguese
            "العربية": "الفصل", // Arabic
            "हिंदी": "अध्याय", // Hindi
            "日本語": "章", // Japanese
            "한국어": "Chapter", // Korean
            "中文": "章", // Chinese
            "اردو": "Chapter", // Urdu
            "Bahasa Melayu": "Bab", // Malay
            "Bahasa Indonesia": "Bab", // Indonesian
            "Tiếng Việt": "Chương", // Vietnamese
            "Slovenský": "Kapitola", // Slovak
        },
    });

    const FormattedText = ({ text, chapterIndex }) => {
        const chapters = useMemo(() => {
            let chapters = text.split("\n\n");
            for (let i = 0; i < chapters.length - 1; i++) {
                chapters[i] = chapters[i] + "\n" + chapters[i + 1];
                chapters.splice(i + 1, 1);
            }

            return chapters.map((chapter, index) => {
                // const [title, ...points] = chapter.replace(/["']/g, "").split('\n').filter(Boolean);
                const [title, ...points] = chapter.split('\n').filter(Boolean);

                return {
                    // title: `Chapter ${Math.max(index + 1, chapterIndex + 1)} - ${title}`,
                    title: `${title}`,
                    points,
                };
            });
        }, [text]);

        return (
            <>
                {chapters.map((chapter, index) => (
                    <>
                        <h1 className={styles.chapterTitle} style={index === 0 ? { marginVertical: 0, marginBottom: 8 } : {}}>
                            {/* {chapter.title.replace(/:/g, "").trim()} */}
                            {chapter.title.trim()}
                        </h1>
                        <div className={styles.bulletPointsContainer}>
                            {chapter.points.map((point, i) => (
                                <div className={styles.bulletPointContainer} key={i}>
                                    <h1 className={styles.bulletPoint}>•</h1>
                                    <h1 className={styles.bulletPointText}>{point.replace(/^-+/, '').trim()}</h1>
                                </div>
                            ))}
                        </div>
                    </>
                ))}
            </>
        );
    };

    // const FormattedText = ({ text, chapterIndex }) => {
    //     const chapter = languageSelected ? translations["chapter"][languageSelected.language] : translations["chapter"]["English"];

    //     const chapters = useMemo(() => {
    //         if (text.indexOf(chapter) < 0 && text.indexOf("Chapter") < 0) {
    //             return [];
    //         }

    //         const chapters = text.indexOf(chapter) < 0 ? text.split("Chapter") : text.split(chapter);
    //         chapters.shift();

    //         return chapters.map((chapter, index) => {
    //             const [title, ...points] = chapter.replace(/["']/g, "").split('\n').filter(Boolean);

    //             return {
    //                 title: `Chapter ${chapterIndex + 1} - ${title}`,
    //                 points,
    //             };
    //         });
    //     }, [text]);

    //     return (
    //         <>
    //             {chapters.map((chapter, index) => (
    //                 <>
    //                     <h1 className={styles.chapterTitle} style={index === 0 ? { marginVertical: 0, marginBottom: 8 } : {}}>
    //                         {chapter.title.replace(/:/g, "").trim()}
    //                     </h1>
    //                     <div className={styles.bulletPointsContainer}>
    //                         {chapter.points.map((point, i) => (
    //                             <div className={styles.bulletPointContainer} key={i}>
    //                                 <h1 className={styles.bulletPoint}>•</h1>
    //                                 <h1 className={styles.bulletPointText}>{point.replace(/^-+/, '').trim()}</h1>
    //                             </div>
    //                         ))}
    //                     </div>
    //                 </>
    //             ))}
    //         </>
    //     );
    // };

    function getLastNonEmptyString(strings) {
        for (let i = strings.length - 1; i >= 0; i--) {
            if (strings[i]) {
                return strings[i];
            }
        }
        return "";
    }

    const handleSummary = async (e) => {
        e.preventDefault();
        if (loadingSummary) {
            e.stopPropagation();
            setLoadingSummary(true);
            return;
        }

        setLoadingSummary(true);

        const input = inputRef.current.value;
        const videoId = await extractVideoId(input);

        if (!videoId) {
            setStatusColor("rgba(255,0,70,1");
            setStatusText("Invalid URL");

            setTimeout(() => {
                setStatusColor("rgba(0,0,0,0)");
                setStatusText("");
            }, 3000);
            setLoadingSummary(false);
            return;
        }

        setStatusColor("rgba(255,200,0,1)");
        setStatusText("Loading Transcript...")

        const { transcript, videoDetails } = await getVideoDetails(videoId);
        const { duration, channel, title, thumbnail, status } = videoDetails;

        if (!transcript) {
            setStatusColor("rgba(255,0,70,1");
            setStatusText("Cannot summarize. Video has no captions.");
            setLoadingSummary(false);
            return;
        }

        setStatusColor("rgba(0,255,100,1");
        setStatusText("Fetched Transcript!");

        setToSummarizeYtAuthor(channel);
        setToSummarizeYtTitle(title);
        setToSummarizeYtThumbnail(thumbnail);
        setToSummarizeYtLength(duration);
        setToSummarizeYtId(videoId);
        setTranscript(transcript);
        setVideoLoaded(true);

        setMessagesDisplay([]);

        let chunks = await splitMessageIntoChunks(transcript, 10000);
        // chunks = chunks.filter((item, index) => item.length > 500 && index !== 0);
        chunks = chunks.map((chunk, index) => {
            // return translations["perMessagePrompt"][languageSelected.language] + chunk
            return prompt + chunk
        });

        for (let i = 0; i < chunks.length; i++) {
            if (stopRequested) {
                stopRequested = false;
                break;
            }
            const chunk = chunks[i];

            const headers = {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + auth,
                "accept": "text/event-stream",
            };

            const data = {
                model: model,
                messages: [
                    { "role": "system", "content": translations["system"][languageSelected.language] },
                    { "role": "user", "content": translations["system"][languageSelected.language] + "." + chunk },
                ],
                temperature: parseInt(temperature),
                stream: true,
            };

            const response = await fetch('https://api.openai.com/v1/chat/completions', { method: 'POST', headers: headers, body: JSON.stringify(data) });
            // const json = await response.json();
            // alert(JSON.stringify(json, null, 2));

            const reader = response.body.getReader();
            const decoder = new TextDecoder();

            let total = "";

            while (true) { // runs while the text stream isn't finished
                const { done, value } = await reader.read();
                if (done || stopRequested) { // conditions to check for when to stop
                    break;
                }

                // data filtration so that key data values can be extracted
                let string = decoder.decode(value).toString();
                let lines = string.split("\n\n");
                let data = getLastNonEmptyString(lines);
                let filtered = data.replace(/data: [DONE]: /g, "");
                filtered = filtered.replace(/data: /g, "");

                try {
                    const message_response = JSON.parse(filtered);
                    const response_message = message_response.choices[0].delta.content;
                    total += response_message;

                    let update = {
                        message: total,
                        from: "assistant",
                        message_id: generateFormattedString(),
                        hidden: false,
                    };

                    console.log(total);

                    setMessagesDisplay(prevMessages => {
                        let copy = [...prevMessages];
                        copy[i] = update;
                        return copy;
                    });
                } catch (error) {
                    // error handling here
                }
            }
        }

        setWriteSummary(true);
        setLoadingSummary(false);
    }

    const handleOpenAIKeysLink = (e) => {
        e.preventDefault();
        window.open("https://platform.openai.com/account/api-keys")
    }

    const handleSaveKey = (e) => {
        e.preventDefault();
        const value = keyRef.current.value;

        // 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
        if (!value.startsWith("sk-") || value.length < 30) {
            alert("Invalid key");
            return;
        }

        setAuth(value);
        localStorage.setItem("vidsummize_auth", value);
    }

    const resetApiKey = (e) => {
        e.preventDefault();
        localStorage.removeItem("vidsummize_auth");
        setAuth(false);
    }

    const toggleLanguageDropdown = (e) => {
        e.preventDefault();
        setLanguageDropdownOpen(!languageDropdownOpen);
    }

    const handleLanguageChange = (e, languageObject) => {
        e.preventDefault();
        setLanguageDropdownOpen(false);
        setLanguageSelected(languageObject);
        setPrompt(translations["perMessagePrompt"][languageObject.language]);
        promptRef.current.value = translations["perMessagePrompt"][languageObject.language];
        localStorage.setItem("vidsummize_language", JSON.stringify(languageObject));
    }

    const handleTempInput = (e) => {
        const value = e.target.value;
        if (value.length === 0) return;
        if (value > 1) return;
        if (value < 0) return;
        setTemperature(value);
    }

    const handleModelChange = (e) => {
        const value = e.target.value;
        setModel(value);
    }

    const handleStop = (e) => {
        e.preventDefault();
        stopRequested = true;
    }

    const handlePromptChange = (e) => {
        e.preventDefault();
        // alert(e.target.value);
        setPrompt(e.target.value);
    }

    const handleLetMeSee = (e) => {
        e.preventDefault();

        const containerTop = letMeSeeRef.current.getBoundingClientRect().top + window.scrollY;

        window.scrollTo({
            top: containerTop,
            behavior: 'smooth'  // smooth scrolling
        });
    }

    const handleGetStarted = (e) => {
        e.preventDefault();

        const containerTop = getStartedRef.current.getBoundingClientRect().top + window.scrollY;

        window.scrollTo({
            top: containerTop,
            behavior: 'smooth'  // smooth scrolling
        });
    }

    return (
        <div className={styles.pageContainer}>
            <header>
                <div className={styles.headerInner}>
                    {/* <div className={styles.backgroundBlurHeader}></div> */}
                    <div className={styles.logoContainer}>
                        <LucideHourglass className={styles.logo} />
                        <h1 className={styles.logoText}>VidSummize</h1>
                    </div>
                    <div className={styles.spacer}>

                    </div>
                    <div className={styles.linksContainer}>
                        <svg onClick={() => { window.open("https://apps.apple.com/us/app/vidsummize-ai-video-summary/id1671335349") }} className={styles.navLogo} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" /></svg>
                        {/* <svg onClick={() => { window.open("https://apps.apple.com/us/app/vidsummize-ai-video-summary/id1671335349") }} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M400 32H48C21.5 32 0 53.5 0 80v352c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48V80c0-26.5-21.5-48-48-48zM127 384.5c-5.5 9.6-17.8 12.8-27.3 7.3-9.6-5.5-12.8-17.8-7.3-27.3l14.3-24.7c16.1-4.9 29.3-1.1 39.6 11.4L127 384.5zm138.9-53.9H84c-11 0-20-9-20-20s9-20 20-20h51l65.4-113.2-20.5-35.4c-5.5-9.6-2.2-21.8 7.3-27.3 9.6-5.5 21.8-2.2 27.3 7.3l8.9 15.4 8.9-15.4c5.5-9.6 17.8-12.8 27.3-7.3 9.6 5.5 12.8 17.8 7.3 27.3l-85.8 148.6h62.1c20.2 0 31.5 23.7 22.7 40zm98.1 0h-29l19.6 33.9c5.5 9.6 2.2 21.8-7.3 27.3-9.6 5.5-21.8 2.2-27.3-7.3-32.9-56.9-57.5-99.7-74-128.1-16.7-29-4.8-58 7.1-67.8 13.1 22.7 32.7 56.7 58.9 102h52c11 0 20 9 20 20 0 11.1-9 20-20 20z" /></svg> */}
                        <svg onClick={() => { window.open("https://play.google.com/store/apps/details?id=com.summarizergpt") }} className={styles.navLogo} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512"><path d="M420.55,301.93a24,24,0,1,1,24-24,24,24,0,0,1-24,24m-265.1,0a24,24,0,1,1,24-24,24,24,0,0,1-24,24m273.7-144.48,47.94-83a10,10,0,1,0-17.27-10h0l-48.54,84.07a301.25,301.25,0,0,0-246.56,0L116.18,64.45a10,10,0,1,0-17.27,10h0l47.94,83C64.53,202.22,8.24,285.55,0,384H576c-8.24-98.45-64.54-181.78-146.85-226.55" /></svg>
                    </div>
                    <div className={styles.themeContainer}>
                        {
                            theme === "dark" ?
                                <LucideSun onClick={() => { setTheme("light"); setLightThemeRoot(); localStorage.setItem("vidsummize_theme", "light"); }} className={styles.themeLogo} />
                                :
                                <LucideMoon onClick={() => { setTheme("dark"); setDarkThemeRoot(); localStorage.setItem("vidsummize_theme", "dark"); }} className={styles.themeLogo} />
                        }
                    </div>
                </div>
                {/* <div className={styles.logoContainer}>
                    <svg className={styles.logo} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><path d="M0 32C0 14.3 14.3 0 32 0H64 320h32c17.7 0 32 14.3 32 32s-14.3 32-32 32V75c0 42.4-16.9 83.1-46.9 113.1L237.3 256l67.9 67.9c30 30 46.9 70.7 46.9 113.1v11c17.7 0 32 14.3 32 32s-14.3 32-32 32H320 64 32c-17.7 0-32-14.3-32-32s14.3-32 32-32V437c0-42.4 16.9-83.1 46.9-113.1L146.7 256 78.9 188.1C48.9 158.1 32 117.4 32 75V64C14.3 64 0 49.7 0 32zM96 64V75c0 25.5 10.1 49.9 28.1 67.9L192 210.7l67.9-67.9c18-18 28.1-42.4 28.1-67.9V64H96zm0 384H288V437c0-25.5-10.1-49.9-28.1-67.9L192 301.3l-67.9 67.9c-18 18-28.1 42.4-28.1 67.9v11z" /></svg>
                    <h1 className={styles.titleText}>VidSummize</h1>
                </div>
                <h1 className={styles.subTitleText}></h1>
                <div className={styles.navBar}>
                    <button className={styles.resetApiKeyButton} onClick={resetApiKey}>Reset API Key</button>

                    <div className={styles.languageDropdownContainer}>
                        <button className={styles.languageButton} onClick={toggleLanguageDropdown}>{languageSelected.flag}</button>
                        <div className={styles.languageDropdown} style={languageDropdownOpen ? { display: "flex" } : { display: "none" }}>
                            {supportedLanguages.filter(item => item.supported).map((language, index) => {
                                return (
                                    <button onClick={(event) => { handleLanguageChange(event, language) }} className={styles.languageSelectButton}>
                                        {!language.limited ? <h1 className={styles.languageSelectText}>{language.flag + " " + language.language}</h1> : <h1 className={styles.languageSelectText} style={{backgroundColor: "rgba(255,0,70,0.25)"}}>{language.flag + " " + language.language} {language.limitedText}</h1>}
                                    </button>
                                )
                            })}
                        </div>
                    </div>
                    <div className={styles.themeContainer}>
                        {
                            theme === "dark" ?
                                <svg onClick={() => { setTheme("light"); setLightThemeRoot(); localStorage.setItem("vidsummize_theme", "light"); }} className={styles.themeLogo} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M361.5 1.2c5 2.1 8.6 6.6 9.6 11.9L391 121l107.9 19.8c5.3 1 9.8 4.6 11.9 9.6s1.5 10.7-1.6 15.2L446.9 256l62.3 90.3c3.1 4.5 3.7 10.2 1.6 15.2s-6.6 8.6-11.9 9.6L391 391 371.1 498.9c-1 5.3-4.6 9.8-9.6 11.9s-10.7 1.5-15.2-1.6L256 446.9l-90.3 62.3c-4.5 3.1-10.2 3.7-15.2 1.6s-8.6-6.6-9.6-11.9L121 391 13.1 371.1c-5.3-1-9.8-4.6-11.9-9.6s-1.5-10.7 1.6-15.2L65.1 256 2.8 165.7c-3.1-4.5-3.7-10.2-1.6-15.2s6.6-8.6 11.9-9.6L121 121 140.9 13.1c1-5.3 4.6-9.8 9.6-11.9s10.7-1.5 15.2 1.6L256 65.1 346.3 2.8c4.5-3.1 10.2-3.7 15.2-1.6zM160 256a96 96 0 1 1 192 0 96 96 0 1 1 -192 0zm224 0a128 128 0 1 0 -256 0 128 128 0 1 0 256 0z" /></svg>
                                :
                                <svg onClick={() => { setTheme("dark"); setDarkThemeRoot(); localStorage.setItem("vidsummize_theme", "dark"); }} className={styles.themeLogo} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><path d="M223.5 32C100 32 0 132.3 0 256S100 480 223.5 480c60.6 0 115.5-24.2 155.8-63.4c5-4.9 6.3-12.5 3.1-18.7s-10.1-9.7-17-8.5c-9.8 1.7-19.8 2.6-30.1 2.6c-96.9 0-175.5-78.8-175.5-176c0-65.8 36-123.1 89.3-153.3c6.1-3.5 9.2-10.5 7.7-17.3s-7.3-11.9-14.3-12.5c-6.3-.5-12.6-.8-19-.8z" /></svg>
                        }
                    </div>
                    <svg className={styles.navLogo} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/></svg>
                    <svg onClick={() => { window.open("https://apps.apple.com/us/app/vidsummize-ai-video-summary/id1671335349") }} className={styles.navLogo} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M400 32H48C21.5 32 0 53.5 0 80v352c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48V80c0-26.5-21.5-48-48-48zM127 384.5c-5.5 9.6-17.8 12.8-27.3 7.3-9.6-5.5-12.8-17.8-7.3-27.3l14.3-24.7c16.1-4.9 29.3-1.1 39.6 11.4L127 384.5zm138.9-53.9H84c-11 0-20-9-20-20s9-20 20-20h51l65.4-113.2-20.5-35.4c-5.5-9.6-2.2-21.8 7.3-27.3 9.6-5.5 21.8-2.2 27.3 7.3l8.9 15.4 8.9-15.4c5.5-9.6 17.8-12.8 27.3-7.3 9.6 5.5 12.8 17.8 7.3 27.3l-85.8 148.6h62.1c20.2 0 31.5 23.7 22.7 40zm98.1 0h-29l19.6 33.9c5.5 9.6 2.2 21.8-7.3 27.3-9.6 5.5-21.8 2.2-27.3-7.3-32.9-56.9-57.5-99.7-74-128.1-16.7-29-4.8-58 7.1-67.8 13.1 22.7 32.7 56.7 58.9 102h52c11 0 20 9 20 20 0 11.1-9 20-20 20z" /></svg>
                    <svg onClick={() => { window.open("https://play.google.com/store/apps/details?id=com.summarizergpt") }} className={styles.navLogo} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512"><path d="M420.55,301.93a24,24,0,1,1,24-24,24,24,0,0,1-24,24m-265.1,0a24,24,0,1,1,24-24,24,24,0,0,1-24,24m273.7-144.48,47.94-83a10,10,0,1,0-17.27-10h0l-48.54,84.07a301.25,301.25,0,0,0-246.56,0L116.18,64.45a10,10,0,1,0-17.27,10h0l47.94,83C64.53,202.22,8.24,285.55,0,384H576c-8.24-98.45-64.54-181.78-146.85-226.55" /></svg>
                </div> */}
            </header>
            <div className={styles.pageContent}>
                <div className={styles.heroContainer}>
                    <h1 className={styles.heroTitle}>VidSummize watches videos for you</h1>
                    <h3 className={styles.heroSubheading}>Leveraging LLMs like ChatGPT and Claude to save you time; consume less, learn more.</h3>
                    <div className={styles.heroButtons}>
                        <button className={styles.getStartedButton} onClick={handleGetStarted}>Get Started <LucideChevronRight /></button>
                        <button className={styles.showMeButton} onClick={handleLetMeSee}>Let me see first!</button>
                    </div>
                </div>
                <div className={`${styles.container} ${styles.featuresContainer}`}>
                    <div className={styles.containerHeader}>
                        <h1 className={styles.containerHeaderTitle} style={{ marginBottom: "50px" }}>Features</h1>
                        <div className={styles.featuresList}>
                            <div className={styles.feature}>
                                <div className={styles.featuresTitleContainer}>
                                    <LucideMic />
                                    <h1 className={styles.featureTitle}>Google Cloud Text To Speech</h1>
                                </div>
                                <h3 className={styles.featureDescription}>Audible summaries, sometimes you don't have the time to read; listen with life-like voices from Google Cloud in 10+ languages.</h3>
                            </div>
                            <div className={styles.feature}>
                                <div className={styles.featuresTitleContainer}>
                                    <LucideGlobe />
                                    <h1 className={styles.featureTitle}>16+ Language Support</h1>
                                </div>
                                <h3 className={styles.featureDescription}>Supports English, Español, Pусский, Português, Deutsch, Français, Italiano, 日本語, 中文, العربية, हिंदी, 한국어, اردو, Bahasa Melayu, Bahasa Indonesia, Tiếng Việt.</h3>
                            </div>
                            <div className={styles.feature}>
                                <div className={styles.featuresTitleContainer}>
                                    <LucideMic2 />
                                    <h1 className={styles.featureTitle}>Follow-Up Questioning</h1>
                                </div>
                                <h3 className={styles.featureDescription}>Able to answer follow-up questions on video.</h3>
                            </div>
                            <div className={styles.feature}>
                                <div className={styles.featuresTitleContainer}>
                                    <LucideBlocks />
                                    <h1 className={styles.featureTitle}>Modularity</h1>
                                </div>
                                <h3 className={styles.featureDescription}>Full customisability of prompt, model, formatting etc.</h3>
                            </div>
                            <div className={styles.feature}>
                                <div className={styles.featuresTitleContainer}>
                                    <LucideScroll />
                                    <h1 className={styles.featureTitle}>Any-length Summation</h1>
                                </div>
                                <h3 className={styles.featureDescription}>Regardless of the length of the video it is able to summarise it.</h3>
                            </div>
                            <div className={styles.feature}>
                                <div className={styles.featuresTitleContainer}>
                                    <LucideArrowBigUp style={{ transform: "scale(1.35)" }} />
                                    <h1 className={styles.featureTitle}>Regular Updates</h1>
                                </div>
                                <h3 className={styles.featureDescription}>VidSummize is always updated to keep up with new advances in technology and new innovative features to save you more time.</h3>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={styles.container}>
                    <div className={styles.previewContainerContainer}>
                        <div className={styles.containerHeader}>
                            <h1 ref={letMeSeeRef} className={styles.containerHeaderTitle}>Sample</h1>
                            <h3 className={styles.containerHeaderDescription}>Click summarize to see a summary generated using VidSummize</h3>
                        </div>
                        <div className={styles.previewContainer}>
                            <div className={styles.previewHeader}>
                                <div className={styles.previewThumbnailContainer}>
                                    <img className={styles.previewThumbnail} src={thumbnail}></img>
                                </div>
                                <div className={styles.previewInformation}>
                                    <h1 className={styles.previewTitle}>3 Paradoxes That Will Change the Way You Think About Everything</h1>
                                    <h3 className={styles.previewDescription}>In this video, we explore 3 essential questions at the foundation of all our knowledge. Through ...</h3>
                                    <div className={styles.previewMetaData}>
                                        <h3>Jun 14, 2023</h3>
                                        <h3>12:41</h3>
                                    </div>
                                </div>
                            </div>
                            {/* <div className={styles.previewChat} style={previewStatus ? { height: "max-height" } : {}}> */}
                            <div className={`${styles.previewChat} ${previewStatus ? styles.open : ""}`}>
                                {previewStatus &&
                                    <div className={styles.botMessageContainer}>
                                        <div className={styles.botLogo}>
                                            <svg width="41" height="41" viewBox="0 0 41 41" fill="none" xmlns="http://www.w3.org/2000/svg" class="icon-md" role="img"><text x="-9999" y="-9999">ChatGPT</text><path d="M37.5324 16.8707C37.9808 15.5241 38.1363 14.0974 37.9886 12.6859C37.8409 11.2744 37.3934 9.91076 36.676 8.68622C35.6126 6.83404 33.9882 5.3676 32.0373 4.4985C30.0864 3.62941 27.9098 3.40259 25.8215 3.85078C24.8796 2.7893 23.7219 1.94125 22.4257 1.36341C21.1295 0.785575 19.7249 0.491269 18.3058 0.500197C16.1708 0.495044 14.0893 1.16803 12.3614 2.42214C10.6335 3.67624 9.34853 5.44666 8.6917 7.47815C7.30085 7.76286 5.98686 8.3414 4.8377 9.17505C3.68854 10.0087 2.73073 11.0782 2.02839 12.312C0.956464 14.1591 0.498905 16.2988 0.721698 18.4228C0.944492 20.5467 1.83612 22.5449 3.268 24.1293C2.81966 25.4759 2.66413 26.9026 2.81182 28.3141C2.95951 29.7256 3.40701 31.0892 4.12437 32.3138C5.18791 34.1659 6.8123 35.6322 8.76321 36.5013C10.7141 37.3704 12.8907 37.5973 14.9789 37.1492C15.9208 38.2107 17.0786 39.0587 18.3747 39.6366C19.6709 40.2144 21.0755 40.5087 22.4946 40.4998C24.6307 40.5054 26.7133 39.8321 28.4418 38.5772C30.1704 37.3223 31.4556 35.5506 32.1119 33.5179C33.5027 33.2332 34.8167 32.6547 35.9659 31.821C37.115 30.9874 38.0728 29.9178 38.7752 28.684C39.8458 26.8371 40.3023 24.6979 40.0789 22.5748C39.8556 20.4517 38.9639 18.4544 37.5324 16.8707ZM22.4978 37.8849C20.7443 37.8874 19.0459 37.2733 17.6994 36.1501C17.7601 36.117 17.8666 36.0586 17.936 36.0161L25.9004 31.4156C26.1003 31.3019 26.2663 31.137 26.3813 30.9378C26.4964 30.7386 26.5563 30.5124 26.5549 30.2825V19.0542L29.9213 20.998C29.9389 21.0068 29.9541 21.0198 29.9656 21.0359C29.977 21.052 29.9842 21.0707 29.9867 21.0902V30.3889C29.9842 32.375 29.1946 34.2791 27.7909 35.6841C26.3872 37.0892 24.4838 37.8806 22.4978 37.8849ZM6.39227 31.0064C5.51397 29.4888 5.19742 27.7107 5.49804 25.9832C5.55718 26.0187 5.66048 26.0818 5.73461 26.1244L13.699 30.7248C13.8975 30.8408 14.1233 30.902 14.3532 30.902C14.583 30.902 14.8088 30.8408 15.0073 30.7248L24.731 25.1103V28.9979C24.7321 29.0177 24.7283 29.0376 24.7199 29.0556C24.7115 29.0736 24.6988 29.0893 24.6829 29.1012L16.6317 33.7497C14.9096 34.7416 12.8643 35.0097 10.9447 34.4954C9.02506 33.9811 7.38785 32.7263 6.39227 31.0064ZM4.29707 13.6194C5.17156 12.0998 6.55279 10.9364 8.19885 10.3327C8.19885 10.4013 8.19491 10.5228 8.19491 10.6071V19.808C8.19351 20.0378 8.25334 20.2638 8.36823 20.4629C8.48312 20.6619 8.64893 20.8267 8.84863 20.9404L18.5723 26.5542L15.206 28.4979C15.1894 28.5089 15.1703 28.5155 15.1505 28.5173C15.1307 28.5191 15.1107 28.516 15.0924 28.5082L7.04046 23.8557C5.32135 22.8601 4.06716 21.2235 3.55289 19.3046C3.03862 17.3858 3.30624 15.3413 4.29707 13.6194ZM31.955 20.0556L22.2312 14.4411L25.5976 12.4981C25.6142 12.4872 25.6333 12.4805 25.6531 12.4787C25.6729 12.4769 25.6928 12.4801 25.7111 12.4879L33.7631 17.1364C34.9967 17.849 36.0017 18.8982 36.6606 20.1613C37.3194 21.4244 37.6047 22.849 37.4832 24.2684C37.3617 25.6878 36.8382 27.0432 35.9743 28.1759C35.1103 29.3086 33.9415 30.1717 32.6047 30.6641C32.6047 30.5947 32.6047 30.4733 32.6047 30.3889V21.188C32.6066 20.9586 32.5474 20.7328 32.4332 20.5338C32.319 20.3348 32.154 20.1698 31.955 20.0556ZM35.3055 15.0128C35.2464 14.9765 35.1431 14.9142 35.069 14.8717L27.1045 10.2712C26.906 10.1554 26.6803 10.0943 26.4504 10.0943C26.2206 10.0943 25.9948 10.1554 25.7963 10.2712L16.0726 15.8858V11.9982C16.0715 11.9783 16.0753 11.9585 16.0837 11.9405C16.0921 11.9225 16.1048 11.9068 16.1207 11.8949L24.1719 7.25025C25.4053 6.53903 26.8158 6.19376 28.2383 6.25482C29.6608 6.31589 31.0364 6.78077 32.2044 7.59508C33.3723 8.40939 34.2842 9.53945 34.8334 10.8531C35.3826 12.1667 35.5464 13.6095 35.3055 15.0128ZM14.2424 21.9419L10.8752 19.9981C10.8576 19.9893 10.8423 19.9763 10.8309 19.9602C10.8195 19.9441 10.8122 19.9254 10.8098 19.9058V10.6071C10.8107 9.18295 11.2173 7.78848 11.9819 6.58696C12.7466 5.38544 13.8377 4.42659 15.1275 3.82264C16.4173 3.21869 17.8524 2.99464 19.2649 3.1767C20.6775 3.35876 22.0089 3.93941 23.1034 4.85067C23.0427 4.88379 22.937 4.94215 22.8668 4.98473L14.9024 9.58517C14.7025 9.69878 14.5366 9.86356 14.4215 10.0626C14.3065 10.2616 14.2466 10.4877 14.2479 10.7175L14.2424 21.9419ZM16.071 17.9991L20.4018 15.4978L24.7325 17.9975V22.9985L20.4018 25.4983L16.071 22.9985V17.9991Z" fill="currentColor"></path></svg>
                                        </div>
                                        <div className={styles.botMessage}>
                                            <FormattedText chapterIndex={0} key={0} text={message} />
                                        </div>
                                    </div>
                                }
                            </div>
                            <div className={styles.previewFunctionContainer}>
                                <button disabled={previewStatus} className={previewStatus ? styles.previewSummarizeButtonActive : styles.previewSummarizeButton} onClick={(e) => { setPreviewStatus(true) }}>
                                    {previewStatus ? !updater ?
                                        <>
                                            Generating
                                            <LucideLoader className={styles.previewSummarizeButtonSpinner} />
                                        </>
                                        :
                                        <>
                                            Generated!
                                            <LucideCheck className={styles.previewSummarizeButtonDone} />
                                        </>
                                        :
                                        <>
                                            Summarize
                                            < LucideChevronRight className={styles.previewSummarizeButtonChevron} />
                                        </>
                                    }
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={`${styles.container} ${styles.getStartedContainer}`}>
                    <div className={styles.containerHeader}>
                        <h1 ref={getStartedRef} className={styles.containerHeaderTitle} style={{ marginBottom: "50px" }}>Getting Started</h1>
                    </div>
                    <div className={styles.gridStarters}>
                        <div onClick={() => { window.open("https://apps.apple.com/us/app/vidsummize-ai-video-summary/id1671335349") }} className={styles.gridStarter}>
                            <h1 className={styles.gridStarterTitle}>iOS App</h1>
                            <svg className={styles.navLogo} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" /></svg>
                        </div>
                        <div onClick={() => { window.open("https://play.google.com/store/apps/details?id=com.summarizergpt") }} className={styles.gridStarter}>
                            <h1 className={styles.gridStarterTitle}>Android App</h1>
                            <svg className={styles.navLogo} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512"><path d="M420.55,301.93a24,24,0,1,1,24-24,24,24,0,0,1-24,24m-265.1,0a24,24,0,1,1,24-24,24,24,0,0,1-24,24m273.7-144.48,47.94-83a10,10,0,1,0-17.27-10h0l-48.54,84.07a301.25,301.25,0,0,0-246.56,0L116.18,64.45a10,10,0,1,0-17.27,10h0l47.94,83C64.53,202.22,8.24,285.55,0,384H576c-8.24-98.45-64.54-181.78-146.85-226.55" /></svg>
                        </div>
                        <div onClick={() => { window.open("https://chrome.google.com/webstore/detail/vidsummize-ai-youtube-sum/gidcfccogfdmkfdfmhfdmfnibafoopic") }} className={styles.gridStarter}>
                            <h1 className={styles.gridStarterTitle}>Chrome Extension</h1>
                            <svg className={styles.navLogo} xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 512 512"><path d="M0 256C0 209.4 12.47 165.6 34.27 127.1L144.1 318.3C166 357.5 207.9 384 256 384C270.3 384 283.1 381.7 296.8 377.4L220.5 509.6C95.9 492.3 0 385.3 0 256zM365.1 321.6C377.4 302.4 384 279.1 384 256C384 217.8 367.2 183.5 340.7 160H493.4C505.4 189.6 512 222.1 512 256C512 397.4 397.4 511.1 256 512L365.1 321.6zM477.8 128H256C193.1 128 142.3 172.1 130.5 230.7L54.19 98.47C101 38.53 174 0 256 0C350.8 0 433.5 51.48 477.8 128V128zM168 256C168 207.4 207.4 168 256 168C304.6 168 344 207.4 344 256C344 304.6 304.6 344 256 344C207.4 344 168 304.6 168 256z" /></svg>
                        </div>
                    </div>
                </div>
                {auth ?
                    <form className={styles.videoInputForm} /* onSubmit={handleSubmit} */ onSubmit={handleSummary}>
                        <div className={styles.previewContainer} style={{ display: videoLoaded ? "flex" : "none" }}>
                            <div className={styles.previewImageContainer}>
                                <img src={toSummarizeYtThumbnail} className={styles.previewImage} />
                            </div>
                            <div className={styles.previewContentContainer}>
                                <h1 className={styles.previewTitle}>
                                    {toSummarizeYtTitle}
                                </h1>
                                <div className={styles.previewAuthor}>
                                    {toSummarizeYtAuthor}
                                </div>
                                <div className={styles.previewDuration}>
                                    {convertSecondsToMinutesAndSeconds(toSummarizeYtLength)}
                                </div>
                            </div>
                        </div>
                        <div className={styles.statusContainer} style={{ backgroundColor: statusColor }}>{statusText}</div>
                        <div className={styles.settingsContainer}>
                            <input defaultValue={0} className={styles.temperatureInput} type="number" min="0" max="1" step="0.01" onInput={handleTempInput} />
                            <select className={styles.modelSelector} id="myDropdown" onChange={handleModelChange}>
                                <option value="gpt-3.5-turbo">gpt-3.5-turbo</option>
                                <option value="gpt-3.5-turbo-0301">gpt-3.5-turbo-0301</option>
                            </select>
                            <textarea ref={promptRef} defaultValue={prompt} className={styles.promptInput} onInput={handlePromptChange} />
                        </div>
                        {/* <h1 style={{color: "var(--secondary-color)", width: "100%", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "20px"}}>🚧 DOWN FOR IMPROVEMENTS 🔧</h1> */}
                        <div className={styles.videoInputContainer}>
                            <input ref={inputRef} placeholder="Enter a YouTube video link or id here" className={styles.videoInput} onKeyPress={(event) => {
                                if (event.key === 'Enter') {
                                    event.preventDefault(); // prevent the default behavior of the Enter key
                                    // handleSubmit(event); // call the function that handles the form submission
                                    handleSummary(event);
                                }
                            }} />
                            {/* <button className={styles.videoInputLoadButton}>
                            <svg className={styles.videoInputSvg} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M498.1 5.6c10.1 7 15.4 19.1 13.5 31.2l-64 416c-1.5 9.7-7.4 18.2-16 23s-18.9 5.4-28 1.6L284 427.7l-68.5 74.1c-8.9 9.7-22.9 12.9-35.2 8.1S160 493.2 160 480V396.4c0-4 1.5-7.8 4.2-10.7L331.8 202.8c5.8-6.3 5.6-16-.4-22s-15.7-6.4-22-.7L106 360.8 17.7 316.6C7.1 311.3 .3 300.7 0 288.9s5.9-22.8 16.1-28.7l448-256c10.7-6.1 23.9-5.5 34 1.4z" /></svg>
                        </button> */}
                        </div>
                        <div className={styles.functionsContainer}>
                            <button className={styles.summarizeButton} onClick={handleSummary}>
                                <svg className={loadingSummary ? styles.summarizeButtonLogoSpin : styles.summarizeButtonLogo} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><path d="M0 32C0 14.3 14.3 0 32 0H64 320h32c17.7 0 32 14.3 32 32s-14.3 32-32 32V75c0 42.4-16.9 83.1-46.9 113.1L237.3 256l67.9 67.9c30 30 46.9 70.7 46.9 113.1v11c17.7 0 32 14.3 32 32s-14.3 32-32 32H320 64 32c-17.7 0-32-14.3-32-32s14.3-32 32-32V437c0-42.4 16.9-83.1 46.9-113.1L146.7 256 78.9 188.1C48.9 158.1 32 117.4 32 75V64C14.3 64 0 49.7 0 32zM96 64V75c0 25.5 10.1 49.9 28.1 67.9L192 210.7l67.9-67.9c18-18 28.1-42.4 28.1-67.9V64H96zm0 384H288V437c0-25.5-10.1-49.9-28.1-67.9L192 301.3l-67.9 67.9c-18 18-28.1 42.4-28.1 67.9v11z" /></svg>
                                <h1 className={styles.summarizeButtonText}>{loadingSummary ? translations["summarizeButtonInUseText"][languageSelected.language] : translations["summarizeButtonText"][languageSelected.language]}</h1>
                            </button>
                            {/* <button className={styles.stopButton} onClick={handleStop}>
                            Stop
                        </button> */}
                        </div>

                        {
                            messagesDisplay.length ? <div className={styles.summaryContainer}>
                                {messagesDisplay.map((message, index) => {
                                    try {
                                        return (
                                            <FormattedText chapterIndex={index} key={index} text={message.message} />
                                        )
                                    } catch (e) {
                                    }
                                })}
                            </div> : <div></div>
                        }

                        {/* <FormattedText chapterIndex={0} text={`Chapter: Inside James's Scam Operation: - James is running a scam where he is converting red diesel to white diesel in his council flat garden, which can be used as regular diesel in cars and is tax-free. - To convert the diesel, they run it through cat litter which removes the red dye, making it look like regular diesel. They sell this diesel in their yard for a cheaper rate than regular white diesel. - James also sublets rooms all over the city, including a basement laundry room which he has turned into a grow farm for cannabis. He claims to pay tenants up to $15,000 per grow. - The house James is in is a council estate, and they rent out the spare rooms to jobless people who need money. One woman even makes up to $60,000 a year for renting out her laundry room for the grow operation. - James believes that what he is doing is helping these people, as some of them are single mothers who need the extra income to support themselves and their families.`} /> */}

                    </form>
                    :
                    // <div>
                    //     <h1 className={styles.disclaimer}>Looks like you haven't setup your OpenAI API key</h1>
                    //     <div className={styles.instructionsContainer}>
                    //         <h1 className={styles.instruction}>1. Click this link <a className={styles.link} href="https://platform.openai.com/account/api-keys">https://platform.openai.com/account/api-keys</a></h1>
                    //         <h1 className={styles.instruction}>2. Generate a new secret key</h1>
                    //         <h1 className={styles.instruction}>3. Copy the secret key</h1>
                    //         <h1 className={styles.instruction}>4. Paste your key into the input box below and click save</h1>
                    //     </div>
                    //     <h1 className={styles.disclaimer}>🔒 We do not store your key anywhere other than your device/browser.</h1>

                    //     <form onSubmit={handleSaveKey} className={styles.keyInputForm}>
                    //         <input type="password" ref={keyRef} placeholder="Enter your OpenAI API key here" className={styles.keyInput} onKeyPress={(event) => { if (event.key === 'Enter') { event.preventDefault(); handleSaveKey(event); } }} />
                    //         <button className={styles.keySaveButton}>Save</button>
                    //     </form>
                    // </div>
                    <div className={styles.frontPage}>

                    </div>
                }
            </div>
        </div>
    )
}